import {
  ColumnTypes,
  type EntityColumn,
} from "common/entities/entity-column/types";
import { getLocalizedName } from "common/index";
import { RecordListSummary } from "common/record/form/content/audit-trail/record-list-summary";
import { getText } from "common/scheduled-event/form/projections/reason";
import type {
  ScheduledWorkOrderRecalculationReasonTypes,
  ReasonOrigin,
  ScheduledWorkOrderRecalculationDueToSiteTimezoneChangeReason,
  ScheduledWorkOrderRecalculationDueToExternalTriggerReason,
  ScheduledWorkOrderRecalculationDueToEntityChangeReason,
  ScheduledWorkOrderRecalculationDueToCalendarChangeReason,
  ScheduledWorkOrderRecalculationDueToWorkOrderEstimatedOnScheduledDateReason,
} from "common/types/reasons";
import type { Context } from "common/types/context";
import { CalendarLabel } from "common/widgets/calendar-selector/label";
import { DateTimeLabel } from "common/widgets/date/date-time-label";
import { getSiteLabelByName } from "x/account-settings/sites/functions";
import { ACTION_MAP, EXTERNAL_TRIGGER_MAP } from "./types";

interface PropTypes {
  context: Context;
  reason: ScheduledWorkOrderRecalculationReasonTypes;
}

const getFakeColumn = (entity: string): EntityColumn => ({
  name: "recordId,",
  dataType: "fk",
  columnType: ColumnTypes.Custom,
  isForeignKey: true,
  relatedEntity: entity,
  relatedColumn: "id",
});

const getAction = (origin: ReasonOrigin): string => {
  return _(ACTION_MAP[origin] || "changed");
};

const getExternalTriggerAction = (origin: ReasonOrigin): string => {
  return _(EXTERNAL_TRIGGER_MAP[origin] || "");
};

const renderCalendarChangeReason = (
  context: Context,
  reason: ScheduledWorkOrderRecalculationDueToCalendarChangeReason,
) => (
  <li className="x-recalculation-reason-item">
    <span className="x-recalculation-reason-item-record">
      <CalendarLabel
        calendars={context.calendars}
        withLinks={true}
        site={context.site.name}
        value={Number(reason.calendarId)}
      />
    </span>
    <span>
      {_("calendar was changed by {USERNAME} on ").replace(
        "{USERNAME}",
        reason.userName,
      )}
    </span>
    <span className="x-recalculation-reason-item-record">
      <DateTimeLabel uiFormat={context.uiFormat} value={reason.occurredOn} />
    </span>
  </li>
);

const renderSiteTimeZoneChangeReason = (
  context: Context,
  reason: ScheduledWorkOrderRecalculationDueToSiteTimezoneChangeReason,
) => (
  <li className="x-recalculation-reason-item">
    <span className="x-recalculation-reason-item-record">
      {_("Timezone of the {SITE_NAME} site was updated by {USER_NAME} on ")
        .replace("{SITE_NAME}", getSiteLabelByName(context.sites, reason.site))
        .replace("{USER_NAME}", reason.userName)}
    </span>
    <span className="x-recalculation-reason-item-record">
      <DateTimeLabel uiFormat={context.uiFormat} value={reason.occurredOn} />
    </span>
  </li>
);

const renderWorkOrderEstimatedOnScheduledDateReason = (
  context: Context,
  reason: ScheduledWorkOrderRecalculationDueToWorkOrderEstimatedOnScheduledDateReason,
) => (
  <li className="x-recalculation-reason-item">
    <span>{_("Scheduled Work Order was estimated on ")}</span>
    <span className="x-recalculation-reason-item-record">
      <DateTimeLabel uiFormat={context.uiFormat} value={reason.occurredOn} />
    </span>
    <span>{_("because some Work Order failed to open")}</span>
  </li>
);

const renderExternalTriggerReason = (
  context: Context,
  reason: ScheduledWorkOrderRecalculationDueToExternalTriggerReason,
) => (
  <li className="x-recalculation-reason-item">
    <span className="x-recalculation-reason-item-record">
      {_(
        "Scheduled Work Order was recalculated from {ACTION} by {USER_NAME} on",
      )
        .replace("{ACTION}", getExternalTriggerAction(reason.origin))
        .replace("{USER_NAME}", reason.userName)}
    </span>
    <span className="x-recalculation-reason-item-record">
      <DateTimeLabel uiFormat={context.uiFormat} value={reason.occurredOn} />
    </span>
  </li>
);

const renderEntityChangeReason = (
  context: Context,
  reason: ScheduledWorkOrderRecalculationDueToEntityChangeReason,
) => {
  const { entityName, recordIds = [], origin } = reason;
  const entity = context.entities[entityName];
  const localizedEntityName = getLocalizedName(entity);

  return (
    <li className="x-recalculation-reason-item">
      <span> {`${localizedEntityName}`} </span>
      <span className="x-recalculation-reason-item-record">
        <RecordListSummary
          context={context}
          entityName={entityName}
          title={localizedEntityName}
          recordIds={recordIds}
          recordsCount={recordIds.length}
          entityColumn={getFakeColumn(entityName)}
          hideLinks={false}
        />
      </span>
      <span>
        {_("was {ACTION} by {USERNAME} on ")
          .replace("{ACTION}", getAction(origin))
          .replace("{USERNAME}", reason.userName)}
      </span>
      <span className="x-recalculation-reason-item-record">
        <DateTimeLabel uiFormat={context.uiFormat} value={reason.occurredOn} />
      </span>
    </li>
  );
};

const renderReasonDetails = (
  context: Context,
  reason: ScheduledWorkOrderRecalculationReasonTypes,
) => {
  const { origin } = reason;

  switch (origin) {
    case "CalendarChanged":
      return renderCalendarChangeReason(context, reason);
    case "SiteTimeZoneChanged":
      return renderSiteTimeZoneChangeReason(context, reason);
    case "WorkOrderEstimatedOnScheduledDate":
      return renderWorkOrderEstimatedOnScheduledDateReason(context, reason);
    case "RecalculationFromApi":
    case "TenantActivated":
    case "TriggerMicroservices":
      return renderExternalTriggerReason(context, reason);
    case "TaskChanged":
    case "TaskItemChanged":
    case "WorkOrderArchived":
    case "WorkOrderEarlyOpened":
    case "WorkOrderOpenedOnScheduledDate":
    case "WorkOrderClosed":
    case "ScheduledWorkOrderAssetsCreated":
    case "ScheduledWorkOrderAssetsUpdated":
    case "ScheduledWorkOrderCreated":
    case "ScheduledWorkOrderUpdated":
    case "ScheduledWorkOrderRestored":
    case "ScheduledWorkOrderArchived":
      return renderEntityChangeReason(context, reason);
    case "ScheduledWorkOrderRecalculated":
      return <li className="x-recalculation-reason-item">{getText(reason)}</li>;
    default:
      return null;
  }
};

export const ScheduledWorkOrderRecalculatedReasonDetails = ({
  context,
  reason,
}: PropTypes) => {
  if (!reason || !Object.keys(reason).length) return <span />;

  return (
    <>
      {"origin" in reason ? (
        <div className="x-recalculation-reason-details-wrapper">
          <legend className="x-event qa-event">
            {_("Recalculation Reason")}
          </legend>
          <ul>{renderReasonDetails(context, reason)}</ul>
        </div>
      ) : undefined}
    </>
  );
};

ScheduledWorkOrderRecalculatedReasonDetails.displayName =
  "ScheduledWorkOrderRecalculatedReasonDetails";
